import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  IconButton,
  Pagination,
  Stack,
  Typography,
} from '@mui/material'
import { X } from 'react-feather'
import {
  Observation,
  ObservationLogsExport,
  ObservationLogsTable,
} from '@/features/observationLogs'
import { AuthorizationGuard, Loader } from '@/components'
import {
  BooleanSelect,
  ManagersMultiSelect,
  ObservationTypesSelect,
  PlaceTypesSelect,
  ResearchersMultiSelect,
  SearchAutocomplete,
  SearchDateField,
  SearchTextField,
  AttentionTaskStatusSelect,
} from '@/components/inputs'
import { EmptyListData } from '@/components/ui'
import {
  useBehaviours,
  useFilters,
  useModal,
  useMutationWrapper,
  useObservationLogs,
  UseObservationLogsSearchParams,
  usePostObservationLogsBulkApprove,
  useSpecies,
} from '@/hooks'
import { theme } from '@/theme'
import { DEFAULT_LIST_LIMIT, DEFAULT_LIST_OFFSET } from '@/constants'
import { useEffect, useState } from 'react'
import { ObservationLog, PaginatedListResponse, UserGroups } from '@/types'
import { ResidenceSelect } from '@/components/inputs/Filters/ResidenceSelect'

export const ObservationLogsContainer = (): JSX.Element => {
  const { t } = useTranslation('observationLogs')
  const { open } = useModal()

  const [selectedItems, setSelectedItems] = useState<
    { id: number; isApproved: boolean }[]
  >([])

  const selectedIds = selectedItems.map((item) => item.id)

  const defaultFiltersParams: UseObservationLogsSearchParams = {
    id: undefined,
    search: undefined,
    createdBy: undefined,
    residence: undefined,
    behaviour: undefined,
    specie: undefined,
    observationType: undefined,
    project_leader: undefined,
    assigned_manager: undefined,
    hasProcessingComment: undefined,
    isApproved: undefined,
    isSoundRecordingsAnalysed: undefined,
    research_visit_event: undefined,
    place_type: undefined,
    task_status: undefined,
    limit: DEFAULT_LIST_LIMIT,
    offset: DEFAULT_LIST_OFFSET,
  }

  const {
    filterParams,
    handleFieldValueChange,
    handleResetFilters,
    initPagesCount,
    pagesCount,
    page,
    onPageChange,
    openDetail,
  } = useFilters<
    UseObservationLogsSearchParams,
    PaginatedListResponse<ObservationLog>,
    ObservationLog
  >(defaultFiltersParams)

  const { data, isLoading } = useObservationLogs(filterParams)
  const species = useSpecies()
  const behaviours = useBehaviours()

  const { mutateAsync: bulkApprove, isLoading: isApproving } =
    useMutationWrapper(usePostObservationLogsBulkApprove)

  useEffect(() => {
    if (data && data.count) {
      initPagesCount(data?.count)

      const detailItemId = openDetail()

      if (detailItemId) {
        openObservationLog(detailItemId)
      }
    }
  }, [data])

  const openObservationLog = (id: number) => {
    if (data && data.results) {
      const index = data.results.findIndex((row) => row.id === id)

      if (index !== -1) {
        open(
          <Observation
            observationLog={data.results[index]}
            showClonedItems={(ids: string) => handleFieldValueChange(ids, 'id')}
          />
        )
      }
    }
  }

  const handleToggleSelect = (id: number, isApproved: boolean) => {
    setSelectedItems((prev) =>
      prev.some((item) => item.id === id)
        ? prev.filter((item) => item.id !== id)
        : [...prev, { id, isApproved }]
    )
  }

  const handleSelectAll = (items: { id: number; isApproved: boolean }[]) => {
    const idsOnPage = items.map((i) => i.id)
    const allSelectedOnPage = idsOnPage.every((id) => selectedIds.includes(id))

    if (allSelectedOnPage) {
      setSelectedItems((prev) =>
        prev.filter((item) => !idsOnPage.includes(item.id))
      )
    } else {
      const newItems = items.filter((item) => !selectedIds.includes(item.id))

      setSelectedItems((prev) => [...prev, ...newItems])
    }
  }

  const handleBulkApprove = async () => {
    const idsToApprove = selectedItems
      .filter((item) => !item.isApproved)
      .map((item) => item.id)

    if (idsToApprove.length > 0) {
      await bulkApprove(idsToApprove, {
        successMessageKey: 'observationLogs:notifications.approved',
        onSuccess: () => {
          setSelectedItems([])
        },
      })
    } else if (selectedItems.length > 0) {
      setSelectedItems([])
    }
  }

  return (
    <Stack>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <Box display="flex" flexWrap="wrap" gap={2} sx={{ p: 2 }}>
          <SearchTextField
            searchValue={filterParams.id as string}
            onChange={(value) => handleFieldValueChange(value, 'id')}
            placeholder="ID"
            searchIcon={false}
            width="120px"
          />
          <SearchTextField
            searchValue={filterParams.search}
            onChange={(value) => handleFieldValueChange(value, 'search')}
            placeholder={t('searchPlaceholder')}
            width="460px"
          />
          <SearchTextField
            searchValue={filterParams.research_visit_event as string}
            onChange={(value) =>
              handleFieldValueChange(value, 'research_visit_event')
            }
            placeholder={t('placeholders.researchVisitEvent')}
            width="240px"
          />
          <AuthorizationGuard
            allowedGroups={[UserGroups.MANAGER, UserGroups.DATA_CONTROLLER]}
            redirect={false}
          >
            <BooleanSelect
              label={t('forms:placeholders.hasProcessingComment')}
              value={filterParams.hasProcessingComment as boolean}
              onChange={(value) =>
                handleFieldValueChange(value, 'hasProcessingComment')
              }
            />
          </AuthorizationGuard>
          <SearchDateField
            searchStartValue={filterParams.startDate as string}
            searchEndValue={filterParams.endDate as string}
            onChangeStartDate={(date) =>
              handleFieldValueChange(date, 'startDate')
            }
            onChangeEndDate={(date) => handleFieldValueChange(date, 'endDate')}
          />
          <ResearchersMultiSelect
            value={filterParams.createdBy as string}
            onChange={(value) => handleFieldValueChange(value, 'createdBy')}
            placeholder={t('placeholders.createdBy')}
          />
          <ResidenceSelect
            value={filterParams.residence}
            onChange={(value) => handleFieldValueChange(value, 'residence')}
          />
          <PlaceTypesSelect
            value={filterParams.place_type as string}
            onChange={(value) => handleFieldValueChange(value, 'place_type')}
            placeholder={t(`placeholders.place_type`)}
          />
          <AttentionTaskStatusSelect
            value={filterParams.task_status as string}
            onChange={(value) => handleFieldValueChange(value, 'task_status')}
          />
          <SearchAutocomplete
            options={species}
            value={filterParams.specie as string}
            onChange={(value) => handleFieldValueChange(value, 'specie')}
            placeholder={t(`placeholders.specie`)}
            sort={true}
            fieldSx={{ width: 315 }}
          />
          <SearchAutocomplete
            options={behaviours}
            value={filterParams.behaviour as string}
            onChange={(value) => handleFieldValueChange(value, 'behaviour')}
            placeholder={t(`placeholders.behaviour`)}
            sort={true}
            fieldSx={{ width: 315 }}
          />
          <ObservationTypesSelect
            value={filterParams.observationType as string}
            onChange={(value) =>
              handleFieldValueChange(value, 'observationType')
            }
            placeholder={t('placeholders.observationType')}
          />
          <AuthorizationGuard
            allowedGroups={[
              UserGroups.MANAGER,
              UserGroups.DATA_CONTROLLER,
              UserGroups.SOUND_TECHNICIAN,
            ]}
            redirect={false}
          >
            <ManagersMultiSelect
              value={filterParams.project_leader as string}
              onChange={(value) =>
                handleFieldValueChange(value, 'project_leader')
              }
              placeholder={t('placeholders.projectsLeaders')}
            />
          </AuthorizationGuard>
          <AuthorizationGuard
            allowedGroups={[UserGroups.MANAGER, UserGroups.DATA_CONTROLLER]}
            redirect={false}
          >
            <ManagersMultiSelect
              value={filterParams.assigned_manager as string}
              onChange={(value) =>
                handleFieldValueChange(value, 'assigned_manager')
              }
              placeholder={t('forms:placeholders.assignedManager')}
            />
          </AuthorizationGuard>
          <BooleanSelect
            label={t('forms:placeholders.hasSoundRecordings')}
            value={filterParams.hasSoundRecordings as boolean}
            onChange={(value) =>
              handleFieldValueChange(value, 'hasSoundRecordings')
            }
          />
          <BooleanSelect
            label={t('forms:placeholders.isSoundRecordingsAnalysed')}
            value={filterParams.isSoundRecordingsAnalysed as boolean}
            onChange={(value) =>
              handleFieldValueChange(value, 'isSoundRecordingsAnalysed')
            }
          />
          <AuthorizationGuard
            allowedGroups={[UserGroups.MANAGER, UserGroups.DATA_CONTROLLER]}
            redirect={false}
          >
            <BooleanSelect
              label={t('forms:placeholders.isApproved')}
              value={filterParams.isApproved as boolean}
              onChange={(value) => handleFieldValueChange(value, 'isApproved')}
            />
          </AuthorizationGuard>
          <AuthorizationGuard
            allowedGroups={[UserGroups.MANAGER, UserGroups.DATA_CONTROLLER]}
            redirect={false}
          >
            {selectedIds.length > 0 && (
              <Button
                variant="contained"
                onClick={handleBulkApprove}
                disabled={isApproving}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {t('buttons.approveSelected', { count: selectedIds.length })}
              </Button>
            )}
          </AuthorizationGuard>
        </Box>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography color="green.600">
            {t('amountWithCount', { count: data?.count ?? 0 })}
          </Typography>
          <IconButton
            onClick={handleResetFilters}
            title={t('buttons.resetFilters')}
          >
            <X size="16px" color={theme.palette.green['500']} />
          </IconButton>
        </Stack>
        <ObservationLogsExport filterParams={{ ...filterParams }} />
      </Stack>

      {data?.count ? (
        <>
          <ObservationLogsTable
            rows={data.results}
            showObservationLogDetails={openObservationLog}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onSelectAll={handleSelectAll}
          />

          {pagesCount > 1 && (
            <Box alignSelf="center" mt={16}>
              <Pagination
                count={pagesCount}
                page={page}
                onChange={onPageChange}
              />
            </Box>
          )}
        </>
      ) : (
        <Box my={20}>
          {isLoading && <Loader />}

          {!isLoading && !data?.count && (
            <EmptyListData textKey="observationLogs:noObservationLogs" />
          )}
        </Box>
      )}
    </Stack>
  )
}
